import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import { simpleLinkColumn } from "@/components/DataTable/Columns/simpleLinkColumn";
import { simpleNumberColumn } from "@/components/DataTable/Columns/simpleNumberColumn";
import { HStack } from "@/components/Layout";
import type { ColumnDef } from "@tanstack/table-core";
import { ShadowItAppsActionsColumn } from "../-components/actions-column";
import type { ListShadowItAppsResponse } from "../-query/type";
import { appCategoryColumn } from "../../-components/app-category-column";
import { ShadowItUser } from "../../-components/shadow-it-user";
import { traceableAppColumn } from "../../-components/traceable-app-column";

type Row = ListShadowItAppsResponse["items"][number];

export const ColumnKey = {
  traceableApp: "traceableApp",
  websiteUrl: "traceableApp.websiteUrl",
  category: "category",
  users: "users",
  countUniqueUser: "countUniqueUser",
  count7Days: "count7DaysHistory",
  count30Days: "count30DaysHistory",
} as const;

export const SortableColumnKey = [
  ColumnKey.countUniqueUser,
  ColumnKey.count7Days,
  ColumnKey.count30Days,
] as const;

function usersColumn(): ColumnDef<Row> {
  return {
    accessorKey: ColumnKey.users,
    meta: { i18nKey: "shadowIt.user", thStyle: { minWidth: "160px" } },
    enableSorting: false,
    header: DataTableColumnHeader,
    cell: ({ getValue }) => {
      const users = getValue<Row["users"]>();

      if (users.length === 1) {
        return <ShadowItUser user={users[0]} showDetail={true} />;
      }

      return (
        <HStack className="gap-3 items-center">
          {/* MEMO: BEからは5件を上限に返している */}
          {users.slice(0, 5).map((user) => (
            <ShadowItUser key={user.id} user={user} />
          ))}
        </HStack>
      );
    },
  };
}

export function useShadowItColumns(): ColumnDef<Row>[] {
  return [
    traceableAppColumn(ColumnKey.traceableApp, {
      i18nKey: "shadowIt.software",
      minWidth: "160px",
      linkable: true,
    }),
    appCategoryColumn(ColumnKey.category, (row) => row.traceableApp.category, {
      i18nKey: "shadowIt.category",
    }),
    usersColumn(),
    simpleLinkColumn(ColumnKey.websiteUrl, {
      i18nKey: "shadowIt.websiteUrl",
      showLeadingIcon: false,
      trailingIcon: "SquareArrowOutUpRightIcon",
    }),
    simpleNumberColumn(ColumnKey.countUniqueUser, {
      i18nKey: "shadowIt.countUniqueUser",
      sortable: true,
    }),
    simpleNumberColumn(ColumnKey.count7Days, {
      i18nKey: "shadowIt.count7DaysDetected",
      sortable: true,
    }),
    simpleNumberColumn(ColumnKey.count30Days, {
      i18nKey: "shadowIt.count30DaysDetected",
      sortable: true,
    }),
    ShadowItAppsActionsColumn(),
  ];
}
